import { Eye, Wrench, Handshake, Code2, Compass, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function About() {
  const { language } = useLanguage();

  const values = [
    {
      icon: Eye,
      title: language === 'pt' ? 'Transparência absoluta' : 'Absolute transparency',
      desc:
        language === 'pt'
          ? 'Sem caixas pretas e sem jargões para confundir. Mostramos o que estamos fazendo, o motivo de cada ação e o impacto direto no seu caixa.'
          : 'No black boxes and no confusing jargon. We show what we are doing, the reason for every action, and the direct impact on your cash flow.',
    },
    {
      icon: Wrench,
      title: language === 'pt' ? 'Foco na execução' : 'Focus on execution',
      desc:
        language === 'pt'
          ? 'Menos apresentações de slides bonitas e mais engrenagens rodando. Nós viemos para sujar as mãos e destravar a sua operação na prática.'
          : 'Fewer pretty slide decks and more moving gears. We are here to get our hands dirty and unlock your operation in practice.',
    },
    {
      icon: Handshake,
      title: language === 'pt' ? 'Parceiro de trincheira' : 'Trench partner',
      desc:
        language === 'pt'
          ? 'Entramos para organizar a base da sua loja e continuamos do seu lado para integrar novos sistemas e escalar os resultados com você.'
          : 'We step in to organize the foundation of your store and stay by your side to integrate new systems and scale results with you.',
    },
  ];

  return (
    <div className="relative">
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-slate-950" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle at top, rgba(34,211,238,0.14), transparent 40%), linear-gradient(to bottom, rgba(15,23,42,0.65), rgba(2,6,23,0.98))',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/90 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-6 font-mono shadow-sm">
            <Code2 className="w-4 h-4" />
            ProcessSurge.Core
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-7xl font-bold text-slate-100 leading-tight tracking-tight mb-6 max-w-5xl mx-auto">
            {language === 'pt' ? 'Não somos agência. Somos o seu motor de escala.' : 'We are not an agency. We are your scaling engine.'}
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            {language === 'pt'
              ? 'A ProcessSurge nasceu para transformar operações de e-commerce caóticas em sistemas previsíveis, automatizados e desenhados para gerar lucro.'
              : 'ProcessSurge was born to transform chaotic e-commerce operations into predictable, automated systems designed to generate profit.'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-12 relative overflow-hidden shadow-2xl shadow-cyan-900/10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px]" />

            <h2 className="font-outfit text-3xl font-bold text-slate-100 mb-8 flex items-center gap-3">
              <Compass className="w-8 h-8 text-purple-400" />
              {language === 'pt' ? 'A lacuna que decidimos preencher' : 'The gap we decided to fill'}
            </h2>

            <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
              <p>
                {language === 'pt'
                  ? 'O mercado atual coloca o empreendedor em uma armadilha. De um lado existem agências focadas apenas em comprar cliques. Do outro lado estão os sistemas corporativos complexos que cobram por dezenas de ferramentas que você nunca vai usar.'
                  : 'The current market puts the entrepreneur in a trap. On one side are agencies focused solely on buying clicks. On the other side are complex corporate systems charging for dozens of tools you will never use.'}
              </p>
              <p>
                {language === 'pt'
                  ? 'Nós nascemos exatamente para resolver esse meio-campo. Somos a equipe técnica que constrói a sua base, recupera as vendas que ficam no carrinho, aplica Inteligência Artificial com pé no chão e integra os seus sistemas operacionais.'
                  : 'We were born exactly to solve this middle ground. We are the technical team that builds your foundation, recovers sales left in the cart, applies grounded AI, and integrates your operational systems.'}
              </p>
              <p className="font-semibold text-white text-xl mt-8">
                {language === 'pt'
                  ? 'Em resumo: nós cuidamos da engrenagem tecnológica para que você possa focar exclusivamente em vender e crescer.'
                  : 'In short: we take care of the technological gears so you can focus entirely on selling and growing.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-outfit text-3xl font-bold text-slate-100 text-center mb-16">
            {language === 'pt' ? 'Os princípios que guiam nosso trabalho' : 'The principles that guide our work'}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((item, i) => (
              <div key={i} className="p-8 bg-slate-950 border border-slate-800 rounded-2xl hover:border-cyan-500/30 transition-colors group">
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-500/20 transition-colors">
                  <item.icon className="w-6 h-6 text-slate-400 group-hover:text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-100 mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-slate-950 border border-slate-800 rounded-2xl">
              <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
                {language === 'pt' ? 'Tecnologia voltada para o lucro' : 'Technology driven by profit'}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {language === 'pt'
                  ? 'Nem todo negócio precisa do pacote de software mais caro do mercado. Nosso trabalho é evitar desperdício de dinheiro e desenhar uma operação enxuta que seja perfeitamente compatível com o seu atual momento financeiro.'
                  : 'Not every business needs the most expensive software package on the market. Our job is to prevent wasted money and design a lean operation perfectly compatible with your current financial stage.'}
              </p>
            </div>

            <div className="p-8 bg-slate-950 border border-slate-800 rounded-2xl">
              <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Handshake className="w-6 h-6 text-cyan-400" />
                {language === 'pt' ? 'Potencializamos o que você já tem' : 'We maximize what you already have'}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {language === 'pt'
                  ? 'Se você já utiliza uma boa plataforma de CRM ou ERP, a ProcessSurge não tenta reinventar a roda. Nós conectamos as suas ferramentas, organizamos os fluxos de dados e fazemos o seu ecossistema trabalhar melhor.'
                  : 'If you already use a good CRM or ERP platform, ProcessSurge does not try to reinvent the wheel. We connect your tools, organize data flows, and make your ecosystem work better.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
